<div class="px-5 pt-10 pb-15 w-full md:w-[768px] xl:w-[1280px] mx-auto">

  <!-- Loading -->
  @if (loading()) {
    <div class="flex justify-center items-center py-20">
      <mat-spinner diameter="48" />
    </div>
  }

  <!-- Not Found -->
  @else if (notFound()) {
    <div class="py-20 text-center">
      <p class="text-gray-400 text-lg">Job not found.</p>
      <a routerLink="/companies" mat-stroked-button class="mt-4">
        Back to Companies
      </a>
    </div>
  }

  @else if (job()) {

    <!-- Breadcrumbs -->
    <nav class="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
      <a routerLink="/" class="hover:text-gray-800 transition-colors">Home</a>
      <span>/</span>
      <a routerLink="/companies" class="hover:text-gray-800 transition-colors">Companies</a>
      <span>/</span>
      <a
        [routerLink]="['/companies', company()?.slug]"
        class="hover:text-gray-800 transition-colors"
      >{{ company()?.name }}</a>
      <span>/</span>
      <span class="text-gray-800 font-medium">{{ jobDetail()?.title }}</span>
    </nav>

    <!-- Inactive Banner -->
    @if (!isActive()) {
      <div class="sticky top-0 z-50 bg-amber-50 border border-amber-200 rounded-2xl mb-4 p-4">
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div class="flex gap-3 items-start">
            <div class="shrink-0 w-9 h-9 rounded-full bg-amber-400 text-white flex items-center justify-center font-bold text-lg mt-0.5">
              !
            </div>
            <div>
              <div class="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wide mb-2">
                No longer available
              </div>
              <div class="text-base font-bold text-amber-900 leading-snug">
                This job is no longer available
              </div>
              <div class="text-sm text-amber-700 mt-1 leading-relaxed">
                The listing below is shown for reference only. You can browse similar jobs or view all current openings.
              </div>
            </div>
          </div>

          <div class="flex gap-2.5 flex-wrap items-center">
            <a
              routerLink="/jobs"
              [queryParams]="similarJobsParams()"
              mat-flat-button
              color="primary"
            >
              View Similar Jobs
            </a>
            <a routerLink="/jobs" mat-stroked-button>
              All Jobs
            </a>
          </div>
        </div>
      </div>
    }

    <!-- Page content — dimmed when inactive -->
    <div [class.opacity-45]="!isActive()" [class.pointer-events-none]="!isActive()">

      <!-- ── Job Header Card ──────────────────────────────────────── -->
      <div class="flex flex-col items-center gap-4 text-center md:flex-row md:items-start md:text-left md:gap-5">
        <!-- Icon -->
        <div class="shrink-0 flex items-center justify-center">
          @if (logo()) {
            <div class="h-48 w-48 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200 md:h-20 md:w-20">
              <img
  [src]="logo()!"
  alt="{{ (company()?.name ?? 'Company') + ' logo' }}"
  width="80"
  height="80"
  class="h-full w-full object-contain p-2"
/>

            </div>
          } @else {
            <div class="flex h-16 w-16 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-gray-200 md:h-20 md:w-20">
              <mat-icon class="text-gray-500">work</mat-icon>
            </div>
          }
        </div>

        <!-- Content -->
        <div class="flex min-w-0 flex-1 flex-col items-center md:items-start">
          <h1 class="text-2xl font-bold tracking-tight text-gray-900 md:text-4xl">
            {{ title_() }}
          </h1>

          @if (company()?.name) {
            <p class="mt-1 text-sm text-gray-600 md:text-base">
              {{ company()?.name }}
            </p>
          }

          <!-- Pills -->
          <div class="mt-3 flex flex-wrap justify-center gap-2 md:justify-start">
            @if (location()) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                📍 {{ location() }}
              </span>
            }

            @if (salary()) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                💰 {{ salary() }}
              </span>
            }

            @if (experience()) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                ⏱️ {{ experience() }}
              </span>
            }

            @if (driverTypes()) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                🚚 {{ driverTypes() }}
              </span>
            }

            @if (freightTypes()) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                📦 {{ freightTypes() }}
              </span>
            }
          </div>
          <!-- Apply -->
       <div class="text-center md:text-left mt-4">
        @if (applyUrl()) {
          <a
            [href]="applyUrl()"
            target="_blank"
            rel="noopener noreferrer"
            mat-flat-button
            color="primary"
            class="w-full sm:w-auto"
          >
            Apply Now →
          </a>
        } @else {
          <button
            mat-flat-button
            color="primary"
            class="w-full sm:w-auto"
            (click)="openApplicationModal()"
          >
            Apply Now →
          </button>
        }
       </div>
        </div>
      </div>
      

      <!-- ── Job Description ─────────────────────────────────────── -->
      <div class="mb-9 mt-9">
        <h2 class="text-2xl font-semibold mt-0 mb-4">Job Description</h2>
        <div
          class="leading-relaxed text-gray-700 text-base bg-white border border-gray-200 rounded-2xl p-7 prose max-w-none"
          [innerHTML]="description() | youtubeEmbed"
        ></div>
      </div>

      <!-- ── Job Location ────────────────────────────────────────── -->
      @if (googleMapsUrl()) {
        <div class="mb-9">
          <div class="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 class="text-2xl font-semibold m-0">Job Location</h2>
            <a
              [href]="googleMapsUrl()"
              target="_blank"
              rel="noopener noreferrer"
              mat-stroked-button
            >
              📍 Open in Google Maps
            </a>
          </div>

          @if (mapLat() && mapLng()) {
            <div class="border border-gray-200 rounded-2xl overflow-hidden relative">
              <div id="tdusa-job-map" class="w-full h-96"></div>
              <a
                [href]="googleMapsUrl()"
                target="_blank"
                rel="noopener noreferrer"
                class="absolute inset-0 z-10 block"
                aria-label="Open in Google Maps"
              ></a>
            </div>
          } @else {
            <div class="border border-gray-200 rounded-2xl bg-gray-50 p-6 text-base text-gray-500">
              📍 {{ location() }}
            </div>
          }

          <p class="text-xs text-gray-400 text-center mt-2 mb-0">
            📍 Location shown is approximate
          </p>
        </div>
      }

      <!-- ── About the Company ───────────────────────────────────── -->
      <div class="mb-9">
        <h2 class="text-2xl font-semibold mt-0 mb-4">About the Company</h2>

        <div class="border border-gray-200 rounded-2xl bg-white p-7 shadow-xs">
          <div class="flex gap-6 items-start flex-wrap">

            <!-- Logo -->
            @if (logo()) {
              <div class="shrink-0 w-40 h-40 border border-gray-100 rounded-2xl bg-white flex items-center justify-center p-4">
                <img
  [src]="logo()"
  [alt]="(company()?.name ?? '') + ' logo'"
  width="160"
  height="160"
  class="w-full h-full object-contain"
/>
              </div>
            }

            <div class="flex-1 min-w-72">
              <h3 class="text-2xl font-semibold mt-0 mb-2">
                {{ company()?.name }}
              </h3>

              @if (companyDescription()) {
                <div
                  class="text-base leading-relaxed text-gray-600 max-w-4xl prose"
                  [innerHTML]="companyDescription() | youtubeEmbed"
                ></div>
              }

              <div class="flex gap-2.5 flex-wrap mt-5">
                <a
                  [routerLink]="['/companies', company()?.slug]"
                  mat-stroked-button
                >
                  View Company
                </a>

                @if (phone()) {
                  <a
                    [href]="'tel:' + phoneHref()"
                    mat-stroked-button
                  >
                    {{ phone() }}
                  </a>
                }
              </div>
            </div>

          </div>
        </div>
      </div>

    </div><!-- end inactive wrapper -->

  }

</div>

<!-- Share / Apply Actions -->
@if (job() && isActive()) {
  <div class="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-50">
    <div class="flex items-center gap-3 justify-between max-sm:flex-col max-sm:items-stretch">
      <!-- Share -->
      <div class="relative">
        <button
          mat-stroked-button
          type="button"
          class="w-full sm:w-auto"
          (click)="toggleShare()"
        >
          Share
        </button>

        @if (shareOpen()) {
          <div class="absolute bottom-full left-0 mb-2 w-64 rounded-2xl border border-gray-200 bg-white p-3 shadow-lg">
            <div class="flex flex-col gap-2">
              <a
                [href]="shareUrl('facebook')"
                target="_blank"
                rel="noopener noreferrer"
                mat-stroked-button
                class="w-full"
              >
                Share on Facebook
              </a>
              <a
                [href]="shareUrl('x')"
                target="_blank"
                rel="noopener noreferrer"
                mat-stroked-button
                class="w-full"
              >
                Share on X
              </a>
              <a
                [href]="shareUrl('sms')"
                target="_blank"
                rel="noopener noreferrer"
                mat-stroked-button
                class="w-full"
              >
                Share via SMS
              </a>
              <button
                mat-flat-button
                color="primary"
                class="w-full"
                (click)="copyLink()"
              >
                {{ copyLabel() }}
              </button>
            </div>
          </div>
        }
      </div>

      <!-- Apply -->
      @if (applyUrl()) {
        <a
          [href]="applyUrl()"
          target="_blank"
          rel="noopener noreferrer"
          mat-flat-button
          color="primary"
          class="w-full sm:w-auto"
        >
          Apply Now →
        </a>
      } @else {
        <button
          mat-flat-button
          color="primary"
          class="w-full sm:w-auto"
          (click)="openApplicationModal()"
        >
          Apply Now →
        </button>
      }
    </div>
  </div>
}